import React, { useMemo, useState } from "react";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import {
  CannedResponse,
  FormGroup,
  Hint,
  NewCannedResponse,
  Plus,
  PrimaryBlueButton,
  RadioLabel,
  Severity,
  TertiaryBlueButton,
  Trash01,
  XClose
} from "@velaro/velaro-component-lib";
import TeamMultiSelect from "@/components/messaging/TeamMultiSelect";

interface Props {
  cannedResonse: CannedResponse | NewCannedResponse;
  onSave: (responses: CannedResponse[]) => void;
  onClose: () => void;
}
export default function CannedResponseRightAsideEditor({
  cannedResonse,
  onSave,
  onClose
}: Props) {
  const isNew = !("id" in cannedResonse);
  const [workingModel, setWorkingModel] = useState(cannedResonse);
  const api = useApi();
  const { displayToast } = useToastAlert();
  const [saving, setSaving] = useState(false);
  const isDirty = useMemo(() => {
    return JSON.stringify(cannedResonse) !== JSON.stringify(workingModel);
  }, [cannedResonse, workingModel]);

  const save = async () => {
    setSaving(true);
    let response: Response | null = null;
    if (isNew) {
      response = await api.messaging.post("CannedResponses", {
        responses: [workingModel]
      });
    } else {
      response = await api.messaging.put("CannedResponses", {
        response: workingModel
      });
    }

    setSaving(false);
    if (!response.ok) {
      displayToast(
        Severity.Error,
        "There was a problem saving the canned response."
      );
      return;
    }

    const data = await response.json();
    onSave(data);
  };

  const newWorkingModel = workingModel as NewCannedResponse;
  const existingWorkingModel = workingModel as CannedResponse;
  return (
    <div className="z-40 fixed right-0 top-0 w-1/3 h-full bg-slate-50 text-slate-800 shadow-lg flex flex-col">
      <h2 className="text-lg font-semibold flex items-center justify-between bg-white border-b border-slate-300 p-6">
        {isNew ? "Add New Response" : cannedResonse.shortcut}
        <XClose className="text-slate-500 cursor-pointer" onClick={onClose} />
      </h2>
      <div className="p-6 h-full overflow-hidden">
        <form
          className="h-full"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <div className="flex flex-col justify-between h-full">
            <div className="overflow-auto grow">
              <div className="mb-4">
                <FormGroup
                  label="Shortcut"
                  direction="column"
                  hint="Enter a unique shortcut used with “/” to quickly insert this response in chat (e.g., /welcome)."
                >
                  <input
                    className="input w-full"
                    type="text"
                    required
                    value={workingModel.shortcut}
                    onChange={(e) =>
                      setWorkingModel({
                        ...workingModel,
                        shortcut: e.target.value
                      })
                    }
                  />
                </FormGroup>
              </div>
              <div className="mb-4">
                <FormGroup
                  label="Message"
                  direction="column"
                  hint="Enter the full canned response message that will be sent in chat. Add more messages to create multiple responses under this shortcut."
                >
                  <>
                    {isNew && (
                      <>
                        <div className="flex flex-col gap-3">
                          {newWorkingModel.messages.map((message, i) => (
                            <div className="relative" key={i}>
                              <textarea
                                value={message}
                                required
                                placeholder="Enter your message"
                                onChange={(e) =>
                                  setWorkingModel({
                                    ...workingModel,
                                    messages: newWorkingModel.messages.map(
                                      (msg, idx) =>
                                        idx === i ? e.target.value : msg
                                    )
                                  })
                                }
                                className="w-full p-4 pr-7 border rounded-md"
                                rows={3}
                              />
                              {newWorkingModel.messages.length > 1 && (
                                <Trash01
                                  className="cursor-pointer absolute right-4 bottom-4 text-slate-500 hover:text-red-500 scale-75"
                                  onClick={() => {
                                    setWorkingModel({
                                      ...workingModel,
                                      messages: newWorkingModel.messages.filter(
                                        (_, idx) => idx !== i
                                      )
                                    });
                                  }}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-end">
                          <TertiaryBlueButton
                            prefixIcon={<Plus />}
                            onClick={() =>
                              setWorkingModel({
                                ...workingModel,
                                messages: [...newWorkingModel.messages, ""]
                              })
                            }
                            label="Add Another Message"
                          />
                        </div>
                      </>
                    )}
                    {!isNew && (
                      <>
                        <textarea
                          value={existingWorkingModel.message}
                          required
                          placeholder="Enter your message"
                          onChange={(e) =>
                            setWorkingModel({
                              ...workingModel,
                              message: e.target.value
                            })
                          }
                          className="w-full p-4 pr-7 border rounded-md"
                          rows={3}
                        />
                      </>
                    )}
                  </>
                </FormGroup>
              </div>
              <FormGroup
                direction="column"
                label="Teams"
                hint="Select which teams can view and use this response."
              >
                <>
                  <RadioLabel checked={workingModel.isGlobal}>
                    <div className="flex gap-4 items-start">
                      <input
                        className="mt-1"
                        type="radio"
                        name="pub-status"
                        checked={workingModel.isGlobal}
                        onChange={(e) => {
                          if (e.currentTarget.checked) {
                            setWorkingModel({
                              ...workingModel,
                              isGlobal: true
                            });
                          }
                        }}
                      />
                      <div className="flex flex-col gap-1">
                        <span>All Teams</span>
                        <Hint>
                          Available to all existing and future teams. Acts as a
                          global canned response.
                        </Hint>
                      </div>
                    </div>
                  </RadioLabel>
                  <RadioLabel checked={!workingModel.isGlobal}>
                    <div className="flex gap-4 items-start">
                      <input
                        className="mt-1"
                        type="radio"
                        name="pub-status"
                        checked={!workingModel.isGlobal}
                        onChange={(e) => {
                          if (e.currentTarget.checked) {
                            setWorkingModel({
                              ...workingModel,
                              isGlobal: false
                            });
                          }
                        }}
                      />
                      <div className="flex flex-col gap-1">
                        <span>Specific Teams</span>
                        <Hint>
                          Limits access to selected teams only. Search and
                          choose which teams can use this canned response.
                        </Hint>
                      </div>
                    </div>
                  </RadioLabel>
                  {!workingModel.isGlobal && (
                    <TeamMultiSelect
                      value={workingModel.teamIds || []}
                      onChange={(teamIds) =>
                        setWorkingModel({
                          ...workingModel,
                          teamIds: teamIds
                        })
                      }
                    />
                  )}
                </>
              </FormGroup>
            </div>
            <div className="flex items-end justify-center py-6 grow">
              <PrimaryBlueButton
                disabled={!isDirty || saving}
                type="submit"
                label={saving ? "Saving..." : "Save Response"}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
